/**
 * DealSourcingTaskDetail — Work Inbox card renderer for the deal cards: tracxn_livedeals (legacy) and first_look (R04, any source).
 *
 * Authority: docs/agents/lead-gen/TRACXN_LIVEDEALS_SPEC.md (Truth Chain C)
 * WI-SHARED-DEAL-CARD-001 PR D: this file is now a thin adapter around the
 * shared DealCard. All rendering logic lives in
 * `frontend/src/components/shared/DealCard.jsx`. The adapter normalises the
 * snapshot dev_tasks payload (which carries `deal_id` instead of `_id`)
 * into the shape DealCard expects, threads the cockpit-owned handlers, and
 * passes `surface="inbox"` so View Deck appears.
 *
 * Pre-WI-SHARED-DEAL-CARD-001 this file owned ~550 lines of duplicated JSX
 * that diverged from the Deal Sourcing agent page. The duplication is now
 * gone — both surfaces render the same DealCard component.
 *
 * Leg 5 follow-up (inbox announcer gap): `onAnnounce` is forwarded straight
 * through to DealCard, which relays it to CockpitLayout's persistent,
 * page-level aria-live region — the region that survives this component
 * unmounting when a successful Pass/Contacted advances `selected` to a
 * different item. See CockpitLayout.jsx and DealCard.jsx for the mechanism.
 */

import React from 'react';
import { DealCard } from '@/components/shared/DealCard';
import { WORK_TYPES } from '@/config/task-display-config';

// The task's own status, in the deal lifecycle words DealCard prints. A first-look card for a company with NO source deal has
// no deal status to merge in, so its actionable state is read from the card itself and nothing is guessed.
const _CARD_STATUS_AS_DEAL_STATUS = {
    proposal_ready: 'pending_review',
    planned: 'pending_review',
    approved: 'contacted',
    rejected: 'passed',
};

export function DealSourcingTaskDetail({ item, onPassDeal, onContacted, onAccept, onReject, onRefresh, onAnnounce }) {
    const p = (item && item.payload) || {};
    // R04 (19 Oct 2026): the generic first-look card is keyed by the company's canonical id; a Tracxn deal, when the company
    // came through one, is a pointer in `deal_id`. The card with a source deal keeps that deal's own Contact / Pass doors; a
    // card with none decides through the card's own accept / reject (read back by the card's id).
    const isFirstLook = item && item.work_type === WORK_TYPES.FIRST_LOOK;
    const sourceDealId = p.deal_id || null;
    const workItemId = item && item.id;
    // Adapter normalisation: DealCard reads deal._id; the dev_tasks payload stores the canonical id under `deal_id`. Spread the
    // payload first so any incidental `_id` field on the snapshot is overwritten by the canonical value.
    const deal = {
        ...p,
        _id: sourceDealId || (isFirstLook ? workItemId : p._id),
        ...(isFirstLook && !sourceDealId && !p.status
            ? { status: _CARD_STATUS_AS_DEAL_STATUS[item.status] || item.status }
            : {}),
    };

    const dealId = deal._id;
    const decidesOnTheCard = isFirstLook && !sourceDealId;

    const handlePass = async (id, reason) => {
        if (decidesOnTheCard) {
            if (!onReject) return;
            await onReject(workItemId, reason || 'passed', item.work_type);
            return;
        }
        if (!onPassDeal) return;
        await onPassDeal(id || dealId, reason || null);
    };

    const handleContacted = async (id) => {
        if (decidesOnTheCard) {
            if (!onAccept) return;
            await onAccept(workItemId, item.work_type, {});
            return;
        }
        if (!onContacted) return;
        // Cockpit's onContacted accepts (workItemId, dealId) so it can
        // remove the matching item from the inbox optimistically.
        await onContacted(workItemId, id || dealId);
    };

    return (
        <DealCard
            deal={deal}
            surface="inbox"
            onPass={handlePass}
            onContacted={handleContacted}
            onRefresh={onRefresh || (() => {})}
            onAnnounce={onAnnounce}
        />
    );
}

export default DealSourcingTaskDetail;
